/**
 * Column headers of the Forecast grid.
 *
 * CR-4726 round 2 column order: Customer | Supplier | Part Number | [PO
 * Number] | [Supplier Part No] | Part Description | [Order Requirements] |
 * UOM | [Stock] | Type | [Firm days | Plant] | Arrears | Weeks | Months |
 * [Future] | Total. Bracketed columns follow the customer's Admin Setup →
 * Forecast switches (decided server-side) or the tenant's modules.
 */
import { ForecastGridLayout } from './forecastGridTypes';

interface ForecastGridHeaderProps {
  layout: ForecastGridLayout;
}

const TH = 'px-3 py-2 text-left font-medium text-slate-700 whitespace-nowrap';

export function ForecastGridHeader({ layout }: ForecastGridHeaderProps) {
  const {
    showPoNumber, showSupplierPartNo, showOrderRequirements, showStockStatus,
    showFirmDays, weeksOnly, weekColumns, monthColumns,
  } = layout;
  return (
    /* CR-5283: thead must sit at z-20 — above the sticky-left Customer body
       cell (z-10). tbody creates no stacking context, so that body cell
       competes at the table level; without this the Customer header was
       painted over by its own column's body cells on vertical scroll. */
    <thead className="bg-slate-100 sticky top-0 z-20">
      <tr>
        {/* Customer pinned sticky-left so horizontal scroll keeps the row's
            owning customer visible. z-30 keeps this frozen top-left corner
            above the sibling headers when scrolling horizontally (CR-5283). */}
        <th className={`${TH} sticky left-0 bg-slate-100 z-30 min-w-[180px] max-w-[220px]`}>
          Customer
        </th>
        <th className={`${TH} min-w-[160px] max-w-[200px] bg-slate-100`}>Supplier</th>
        <th className={`${TH} min-w-[120px] bg-slate-100`}>Part Number</th>
        {/* Customer review 11 Sep (item 35a): off unless the customer
            switches it on (default on only for part-scheduling tenants). */}
        {showPoNumber && (
          <th className={`${TH} min-w-[120px] bg-slate-100`} title="Open PO number(s) carrying this part's demand. Forecast rows never carry one unless they are PO schedules beyond the firm window.">
            PO Number
          </th>
        )}
        {showSupplierPartNo && (
          <th className={`${TH} min-w-[120px] bg-slate-100`} title="The supplier's own part reference, published by the ERP.">
            Supplier Part No
          </th>
        )}
        {/* CR-3854: Part Description column */}
        <th className={`${TH} min-w-[180px] bg-slate-100`}>Part Description</th>
        {/* Customer review 11 Sep (item 35b): the PO line's own text,
            kept out of the description when it comes from the part master. */}
        {showOrderRequirements && (
          <th className={`${TH} min-w-[200px] bg-slate-100`} title="Requirements written on the latest purchase order line for this part (certificates, approved sources). Hover a cell for the full text.">
            Order Requirements
          </th>
        )}
        {/* CR-4726 round 2: UOM column added per spec. */}
        <th className={`${TH} min-w-[70px] bg-slate-100`}>UOM</th>
        {showStockStatus && (
          <th className="px-2 py-2 text-center font-medium text-slate-700 whitespace-nowrap min-w-[50px] bg-slate-100">
            Stock
          </th>
        )}
        <th className={`${TH} min-w-[100px]`}>Type</th>
        {showFirmDays && (
          <th className="px-3 py-2 text-right font-medium text-slate-700 whitespace-nowrap min-w-[80px]" title="Firm boundary in days (PartSchedule / PartScheduleEntry schedule_firm). PO demand with delivery date within this window is Open PO; beyond is Forecast.">
            Firm days
          </th>
        )}
        {showFirmDays && (
          <th className={`${TH} min-w-[70px]`} title="Plant of the active part-schedule entry (PartScheduleEntry is per part and plant).">
            Plant
          </th>
        )}
        <th className="px-3 py-2 text-right font-medium text-slate-700 whitespace-nowrap min-w-[80px]">
          Arrears
        </th>
        {weekColumns.map((col) => (
          <th
            key={col.key}
            className="px-2 py-2 text-right font-medium text-slate-700 whitespace-nowrap min-w-[70px] bg-blue-50"
            title={col.tooltip}
          >
            {col.label}
          </th>
        ))}
        {monthColumns.map((col) => (
          <th
            key={col.key}
            className="px-2 py-2 text-right font-medium text-slate-700 whitespace-nowrap min-w-[70px] bg-amber-50"
            title={col.tooltip}
          >
            {col.label}
          </th>
        ))}
        {/* Future hidden in weekly-horizon mode */}
        {!weeksOnly && (
          <th className="px-3 py-2 text-right font-medium text-slate-700 whitespace-nowrap min-w-[80px] bg-slate-200">
            Future
          </th>
        )}
        <th className="px-3 py-2 text-right font-medium text-slate-700 whitespace-nowrap min-w-[90px] bg-slate-200">
          Total
        </th>
      </tr>
    </thead>
  );
}

/**
 * Number of columns the header renders — the empty-state cell spans them all.
 *
 * @param layout - the grid layout
 * @returns the column count
 */
export function forecastColumnCount(layout: ForecastGridLayout): number {
  const flags = [
    layout.showPoNumber, layout.showSupplierPartNo, layout.showOrderRequirements,
    layout.showStockStatus, layout.showFirmDays, layout.showFirmDays, !layout.weeksOnly,
  ];
  // Customer, Supplier, Part Number, Part Description, UOM, Type, Arrears, Total
  const fixed = 8;
  return fixed + flags.filter(Boolean).length + layout.weekColumns.length + layout.monthColumns.length;
}
